import { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CalendarIcon } from "lucide-react";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";
import { useDashboard } from "@/contexts/SyncContext";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import {
  applyReportDateToSearchParams,
  formatYmdLocal,
  parseReportDateSearchParams,
  reportDateFilterToQueryString,
  type ReportDatePreset,
} from "@/lib/reportDateFilterUrl";
import type { DateRange as DayPickerRange } from "react-day-picker";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

function getDateRangeForPeriod(period: ReportDatePreset): { start: Date | null; end: Date | null } {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

  switch (period) {
    case "1W": {
      const start = new Date(now);
      start.setDate(start.getDate() - 7);
      start.setHours(0, 0, 0, 0);
      return { start, end };
    }
    case "1M": {
      const start = new Date(now);
      start.setMonth(start.getMonth() - 1);
      start.setHours(0, 0, 0, 0);
      return { start, end };
    }
    case "MTD": {
      const start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
      return { start, end };
    }
    case "YTD": {
      const start = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
      return { start, end };
    }
    case "All":
      return { start: null, end: null };
  }
}

function buildFilledDailyRows({
  start,
  end,
  sparse,
}: {
  start: Date;
  end: Date;
  sparse: { date: string; revenue: number; contacts: number; active: number }[];
}) {
  const map = new Map(sparse.map((r) => [r.date, r]));
  const rows: {
    key: string;
    label: string;
    revenue: number;
    contacts: number;
    active: number;
  }[] = [];
  const d = new Date(start);
  d.setHours(0, 0, 0, 0);
  const endDay = new Date(end);
  endDay.setHours(0, 0, 0, 0);
  while (d <= endDay) {
    const key = formatYmdLocal(d);
    const existing = map.get(key);
    rows.push({
      key,
      label: d.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      revenue: (existing?.revenue ?? 0) / 100,
      contacts: existing?.contacts ?? 0,
      active: existing?.active ?? 0,
    });
    d.setDate(d.getDate() + 1);
  }
  return rows;
}

function CardDailyChart({
  dataKey,
  data,
  formatValue,
  colorVar,
}: {
  dataKey: "revenue" | "contacts" | "active";
  data: { key: string; label: string; revenue: number; contacts: number; active: number }[];
  formatValue: (value: number) => string;
  colorVar: string;
}) {
  return (
    <div className="h-24 w-full mt-3 -mx-1">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis dataKey="label" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
          <YAxis hide domain={[0, "auto"]} />
          <Tooltip
            formatter={(value: number | undefined) => [formatValue(value ?? 0), undefined]}
            labelFormatter={(_, items) => {
              const row = items?.[0]?.payload as { label?: string } | undefined;
              return row?.label ?? "";
            }}
          />
          <Area
            type="monotone"
            dataKey={dataKey}
            stroke={colorVar}
            fill={colorVar}
            fillOpacity={0.15}
            strokeWidth={2}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function Dashboard() {
  const [, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { preset, customRange } = useMemo(
    () =>
      parseReportDateSearchParams(new URLSearchParams(location.search), {
        defaultPreset: "1W",
      }),
    [location.search],
  );
  const [tempDateRange, setTempDateRange] = useState<DayPickerRange | undefined>(undefined);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const { data, loading } = useDashboard(
    customRange ? undefined : preset,
    customRange?.from,
    customRange?.to,
  );
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  const chartRows = useMemo(() => {
    if (!data?.daily) return null;
    const { start, end } =
      customRange?.from && customRange?.to
        ? { start: customRange.from, end: customRange.to }
        : getDateRangeForPeriod(preset);
    if (!start || !end) return null;
    return buildFilledDailyRows({ start, end, sparse: data.daily });
  }, [data, preset, customRange]);

  useEffect(() => {
    setBreadcrumbs([]);

    return () => {
      clearBreadcrumbs();
    };
  }, []);

  if (loading || !data || !data.payments) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  const periods: ReportDatePreset[] = ["1W", "1M", "MTD", "YTD", "All"];

  const tileSearch = reportDateFilterToQueryString({ preset, customRange });
  const isAllTime = preset === "All" && !customRange;

  const formatPeriodDateRange = (period: ReportDatePreset): string => {
    if (period === "All") return "All";
    const { start, end } = getDateRangeForPeriod(period);
    if (start && end) {
      return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
    }
    return period;
  };

  const handlePeriodClick = (period: ReportDatePreset) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        applyReportDateToSearchParams(next, { preset: period, customRange: undefined });
        return next;
      },
      { replace: true },
    );
  };

  const handleDateRangeSelect = (range: DayPickerRange | undefined) => {
    setTempDateRange(range);
  };

  const handleDone = () => {
    if (tempDateRange?.from && tempDateRange?.to) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          applyReportDateToSearchParams(next, { preset: "All", customRange: tempDateRange });
          return next;
        },
        { replace: true },
      );
    }
    setCalendarOpen(false);
  };

  const handleCancel = () => {
    setTempDateRange(customRange);
    setCalendarOpen(false);
  };

  const handleOpenChange = (open: boolean) => {
    setCalendarOpen(open);
    if (open) {
      setTempDateRange(customRange);
    }
  };

  const formatDateRange = () => {
    if (customRange?.from && customRange?.to) {
      return `${customRange.from.toLocaleDateString()} - ${customRange.to.toLocaleDateString()}`;
    }
    return "Custom Range";
  };

  return (
    <div className="p-4">
      <div className="flex gap-2 mb-4 flex-wrap items-center">
        {periods.map((period) => (
          <Button
            key={period}
            variant={preset === period && !customRange ? "default" : "outline"}
            size="sm"
            onClick={() => handlePeriodClick(period)}
          >
            {period}
          </Button>
        ))}
        <Popover open={calendarOpen} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <Button
              variant={customRange || (preset !== "All" && !customRange) ? "default" : "outline"}
              size="sm"
              className="gap-2"
            >
              <CalendarIcon className="h-4 w-4" />
              {customRange?.from && customRange?.to
                ? formatDateRange()
                : preset !== "All" && !customRange
                ? formatPeriodDateRange(preset)
                : "Custom Range"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="range"
              selected={tempDateRange}
              onSelect={handleDateRangeSelect}
              numberOfMonths={1}
              className="rounded-md border-0"
            />
            <div className="flex justify-end gap-2 p-3 border-t">
              <Button variant="outline" size="sm" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleDone}
                disabled={!tempDateRange?.from || !tempDateRange?.to}
              >
                Done
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {isAllTime ? (
          <>
            <Link to={tileSearch ? `/contacts?${tileSearch}` : "/contacts"}>
              <Card className="cursor-pointer hover:bg-accent transition-colors">
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold">
                      {data.leads.count.toLocaleString()}
                    </p>
                    <p className="text-muted-foreground mt-2">Contacts</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
            <Link to={tileSearch ? `/sales/orders?${tileSearch}` : "/sales/orders"}>
              <Card className="cursor-pointer hover:bg-accent transition-colors">
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold">
                      ${(data.payments.totalRevenue / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                    <p className="text-muted-foreground mt-2">Revenue</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </>
        ) : (
          <>
            <Link to={tileSearch ? `/contacts?${tileSearch}` : "/contacts"}>
              <Card className="cursor-pointer hover:bg-accent transition-colors">
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold">
                      {data.leads.count.toLocaleString()}
                    </p>
                    <p className="text-muted-foreground mt-2">New Contacts</p>
                  </div>
                  {chartRows && (
                    <CardDailyChart
                      dataKey="contacts"
                      data={chartRows}
                      colorVar="var(--chart-3)"
                      formatValue={(v) => Math.round(v).toLocaleString()}
                    />
                  )}
                </CardContent>
              </Card>
            </Link>
            <Link
              to={
                tileSearch ?
                  `/contacts?segment=active&${tileSearch}`
                : "/contacts?segment=active"
              }
            >
              <Card className="cursor-pointer hover:bg-accent transition-colors">
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold">
                      {(data.leads.activeCount ?? 0).toLocaleString()}
                    </p>
                    <p className="text-muted-foreground mt-2">Active Contacts</p>
                  </div>
                  {chartRows && (
                    <CardDailyChart
                      dataKey="active"
                      data={chartRows}
                      colorVar="var(--chart-2)"
                      formatValue={(v) => Math.round(v).toLocaleString()}
                    />
                  )}
                </CardContent>
              </Card>
            </Link>
            <Link
              to={tileSearch ? `/sales/orders?${tileSearch}` : "/sales/orders"}
              className="md:col-span-2"
            >
              <Card className="cursor-pointer hover:bg-accent transition-colors">
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold">
                      ${(data.payments.totalRevenue / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                    <p className="text-muted-foreground mt-2">Revenue</p>
                  </div>
                  {chartRows && (
                    <CardDailyChart
                      dataKey="revenue"
                      data={chartRows}
                      colorVar="var(--chart-1)"
                      formatValue={(v) =>
                        `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      }
                    />
                  )}
                </CardContent>
              </Card>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
